import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { usePeopleMutations } from '@/hooks/use-people'
import type { Invitation, Person } from '@/lib/bff/people'
import { RolePills } from '@/components/visitone/surfaces/people/people-shared'
import { errorCode, errorStatus, personName } from '@/lib/people/people-text'

type ModalProblem = 'failed' | 'refused' | 'blocked' | 'accepted' | null

function Refused() {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  return (
    <OperationFeedback
      state="error"
      title={t('core.access.actionRefusedTitle')}
      detail={t('core.access.actionRefusedBody', { tenant })}
    />
  )
}

function ModalHeading({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 id={`${id}-title`} className="text-lg font-semibold text-fg-strong wrap-anywhere">
        {title}
      </h2>
      <div id={`${id}-body`} className="mt-1 flex flex-col gap-2 text-sm text-fg-muted wrap-anywhere">
        {children}
      </div>
    </div>
  )
}

/** The working status of a modal's action, announced politely. */
function Working({ text }: { text: string | null }) {
  return (
    <p role="status" className="sr-only">
      {text ?? ''}
    </p>
  )
}

/*
 * Remove access (VIS-332): another person's, or the caller's own when another
 * Organisation Admin remains. A guard-rail refusal (the last Organisation
 * Admin, or access changed meanwhile) leaves only Close.
 */
export function RemoveAccessModal({
  person,
  otherOrganisationAdmin,
  onClose,
  onRemoved,
}: {
  person: Person | null
  /** Another Organisation Admin by name, for the self-removal body; null when none is known from the loaded page. */
  otherOrganisationAdmin: string | null
  onClose: () => void
  onRemoved: (person: Person) => void
}) {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  const mutations = usePeopleMutations()
  const [problem, setProblem] = React.useState<ModalProblem>(null)
  const [busy, setBusy] = React.useState(false)
  const [shownFor, setShownFor] = React.useState(person)
  if (person !== shownFor) {
    setShownFor(person)
    setProblem(null)
  }

  const confirm = async () => {
    if (!person || busy) return
    setBusy(true)
    setProblem(null)
    try {
      await mutations.removeAccess(person.userId)
      onRemoved(person)
    } catch (error) {
      const code = errorCode(error)
      if (code === 'last_organisation_admin' || code === 'changed_meanwhile' || errorStatus(error) === 404) {
        setProblem('blocked')
        void mutations.revalidate()
      } else if (errorStatus(error) === 403) {
        setProblem('refused')
      } else {
        setProblem('failed')
      }
    } finally {
      setBusy(false)
    }
  }

  const self = person?.isSelf === true
  const name = person ? personName(person) : ''
  const blocked = problem === 'blocked'
  return (
    <Modal open={person !== null} onClose={onClose} labelledBy="remove-access-title" describedBy="remove-access-body">
      {person ? (
        <div className="flex flex-col gap-4">
          <ModalHeading
            id="remove-access"
            title={self ? t('people.remove.selfTitle', { tenant }) : t('people.remove.title', { name, tenant })}
          >
            <p>
              {self
                ? otherOrganisationAdmin
                  ? t('people.remove.selfBody', { other: otherOrganisationAdmin, tenant })
                  : t('people.remove.selfBodyAnother', { tenant })
                : t('people.remove.body', { name, email: person.email ?? name, tenant })}
            </p>
          </ModalHeading>
          {blocked ? (
            <OperationFeedback
              state="error"
              title={t('people.remove.blockedTitle')}
              detail={t('people.remove.blockedBody', { tenant })}
            />
          ) : null}
          {problem === 'failed' ? (
            <OperationFeedback state="error" title={t('people.remove.blockedTitle')} detail={t('people.remove.failedBody')} />
          ) : null}
          {problem === 'refused' ? <Refused /> : null}
          <Working text={busy ? t('people.remove.working') : null} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" autoFocus onClick={onClose}>
              {blocked ? t('people.remove.close') : t('people.remove.cancel')}
            </Button>
            {blocked ? null : (
              <Button variant="destructive" aria-disabled={busy || undefined} onClick={() => void confirm()}>
                {self ? t('people.remove.confirmSelf') : t('people.remove.confirm')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}

/*
 * Withdraw an invitation (VIS-332). If it was accepted while the dialog was
 * open there is nothing to withdraw, and only Close remains.
 */
export function WithdrawInvitationModal({
  invitation,
  onClose,
  onWithdrawn,
}: {
  invitation: Invitation | null
  onClose: () => void
  onWithdrawn: (invitation: Invitation) => void
}) {
  const t = useT()
  const { tenantName: tenant } = useAccess()
  const mutations = usePeopleMutations()
  const [problem, setProblem] = React.useState<ModalProblem>(null)
  const [busy, setBusy] = React.useState(false)
  const [shownFor, setShownFor] = React.useState(invitation)
  if (invitation !== shownFor) {
    setShownFor(invitation)
    setProblem(null)
  }

  const confirm = async () => {
    if (!invitation || busy) return
    setBusy(true)
    setProblem(null)
    try {
      await mutations.withdraw(invitation.id)
      onWithdrawn(invitation)
    } catch (error) {
      const code = errorCode(error)
      if (code === 'changed_meanwhile' || code === 'invitation_not_usable' || errorStatus(error) === 404) {
        setProblem('accepted')
        void mutations.revalidate()
      } else if (errorStatus(error) === 403) {
        setProblem('refused')
      } else {
        setProblem('failed')
      }
    } finally {
      setBusy(false)
    }
  }

  const email = invitation?.email ?? ''
  const accepted = problem === 'accepted'
  return (
    <Modal open={invitation !== null} onClose={onClose} labelledBy="withdraw-invitation-title" describedBy="withdraw-invitation-body">
      {invitation ? (
        <div className="flex flex-col gap-4">
          <ModalHeading id="withdraw-invitation" title={t('people.withdraw.title', { email })}>
            <p>{t('people.withdraw.body', { tenant })}</p>
          </ModalHeading>
          {accepted ? (
            <OperationFeedback
              state="error"
              title={t('people.withdraw.acceptedTitle')}
              detail={t('people.withdraw.acceptedBody', { email })}
            />
          ) : null}
          {problem === 'failed' ? (
            <OperationFeedback state="error" title={t('people.withdraw.failedTitle')} detail={t('people.withdraw.failedBody')} />
          ) : null}
          {problem === 'refused' ? <Refused /> : null}
          <Working text={busy ? t('people.withdraw.working') : null} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" autoFocus onClick={onClose}>
              {accepted ? t('people.withdraw.close') : t('people.withdraw.cancel')}
            </Button>
            {accepted ? null : (
              <Button variant="destructive" aria-disabled={busy || undefined} onClick={() => void confirm()}>
                {t('people.withdraw.confirm')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}

/*
 * Send an invitation again (VIS-332): the current one is withdrawn and a new
 * one goes to the same address with the same roles.
 */
export function SendAgainModal({
  invitation,
  onClose,
  onSent,
}: {
  invitation: Invitation | null
  onClose: () => void
  onSent: (previous: Invitation, replacement: Invitation) => void
}) {
  const t = useT()
  const mutations = usePeopleMutations()
  const [problem, setProblem] = React.useState<ModalProblem>(null)
  const [busy, setBusy] = React.useState(false)
  const [shownFor, setShownFor] = React.useState(invitation)
  if (invitation !== shownFor) {
    setShownFor(invitation)
    setProblem(null)
  }

  const confirm = async () => {
    if (!invitation || busy) return
    setBusy(true)
    setProblem(null)
    try {
      const replacement = await mutations.sendAgain(invitation.id)
      onSent(invitation, replacement)
    } catch (error) {
      if (errorStatus(error) === 403) setProblem('refused')
      else {
        setProblem('failed')
        if (errorCode(error) === 'changed_meanwhile' || errorStatus(error) === 404) void mutations.revalidate()
      }
    } finally {
      setBusy(false)
    }
  }

  const email = invitation?.email ?? ''
  return (
    <Modal open={invitation !== null} onClose={onClose} labelledBy="send-again-title" describedBy="send-again-body">
      {invitation ? (
        <div className="flex flex-col gap-4">
          <ModalHeading id="send-again" title={t('people.sendAgain.title', { email })}>
            <p>{t('people.sendAgain.body', { email })}</p>
            <RolePills roles={invitation.roles} label={t('people.invitations.rolesColumn')} />
          </ModalHeading>
          {problem === 'failed' ? (
            <OperationFeedback state="error" title={t('people.sendAgain.failedTitle')} detail={t('people.sendAgain.failedBody')} />
          ) : null}
          {problem === 'refused' ? <Refused /> : null}
          <Working text={busy ? t('people.sendAgain.working') : null} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" autoFocus onClick={onClose}>
              {t('people.sendAgain.cancel')}
            </Button>
            <Button variant="primary" aria-disabled={busy || undefined} onClick={() => void confirm()}>
              {t('people.sendAgain.confirm')}
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
